// src/judge/dualPicks.ts
//
// Dual mód (dva rozhodčí u jednoho stanoviště): rezervace střelců
// v races/{raceId}/live_dual_roles/{stationId}_{runda} – { activePicks:
// { [startovní číslo]: uid } } – a upozornění na převzetí
// ({stationId}_{runda}_notif).
//
// Fáze 3 (D3, rozhodnutí majitele 7′ – docs/live-faze3-navrh.md bod 5):
// - se spojením rezervace a převzetí transakcí: jednoho střelce nejde vzít
//   dvakrát (kdo přijde druhý, dostane „taken“); chybějící dokument
//   rezervací transakce založí;
// - bez spojení (nebo když transakce kvůli spojení selže či nestihne
//   TX_WAIT_MS) se rezervace zařadí do fronty zápisem po polích (merge –
//   rezervace druhého rozhodčího se nikdy nepřepíšou) a volající ukáže
//   varování OFFLINE_WARNING; dual tak jde bodovat i bez signálu;
// - uvolnění vždy zápis po polích (deleteField), nikdy transakce, nikdo na
//   něj nečeká (bez signálu by se slib nesplnil).
// Všechny zápisy jen activePicks.<startovní číslo> (merge) – dřív se
// přepisovala celá mapa a zápis bez signálu mohl smazat rezervaci partnera.
//
// Po vypršení TX_WAIT_MS se transakce „opustí“: SDK ji bez signálu dál
// opakuje a pozdní pokus by nad novým stavem serveru znovu rezervoval už
// uvolněného / dobodovaného střelce nebo smazal novější rezervaci – funkce
// transakce proto po opuštění vyhodí chybu (tu SDK neopakuje). Pokus, který
// už prošel, se ohlásí volajícímu (onLate: „taken“ / „picked“), stejně jako
// výsledek zápisu ve frontě („saved“ / „rejected“).
//
// Přijatá rizika (návrh bod 6): po vypršení limitu jde rezervace do fronty
// a může přepsat rezervaci, kterou si partner mezitím udělal – partner se to
// nedozví (rozhodčí dostane hlášku „taken“, jen když to ohlásí transakce).
// Uvolnění maže podle posledního známého stavu: v nepatrném okně by smazalo
// převzetí, které partner právě udělal (uvolnění nikdy transakcí – 7′).
//
// Smíšené verze: starší aplikace zapisuje celou mapu (setDoc bez merge)
// a mohla by novou rezervaci přepsat – řeší ji upozornění „nová verze
// aplikace“ (I9) a obnovení zařízení před závodem; značky ve schématu ne.

import { deleteField, doc, getDoc, runTransaction, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { JUDGE_NAME_FALLBACK } from "../utils/shooterName";

export type ActivePicks = Record<string, string>;

/** Výsledek rezervace / převzetí. */
export type PickOutcome = "picked" | "taken" | "queued";

/**
 * Pozdní zpráva po „queued“: transakce, která už byla na cestě, nakonec
 * prošla („picked“) nebo zjistila obsazeného střelce („taken“); zápis ve
 * frontě server potvrdil („saved“) nebo zamítl („rejected“).
 */
export type LateOutcome = "picked" | "taken" | "saved" | "rejected";
export type OnLate = (late: LateOutcome) => void;

/** Chyba funkce transakce po jejím opuštění – SDK ji neopakuje. */
class AbandonedTransaction extends Error {
  constructor() {
    super("Transakce rezervace opuštěná po vypršení limitu.");
  }
}

/** Varování po rezervaci bez spojení (rozhodnutí 7′). */
export const DUAL_OFFLINE_WARNING = "Bez spojení – domluvte se s druhým rozhodčím.";

/** Nejdéle tak dlouho se čeká na transakci, pak se rezervace zařadí do fronty. */
export const TX_WAIT_MS = 6000;

const dualRef = (raceId: string, roleKey: string) => doc(db, `races/${raceId}/live_dual_roles`, roleKey);
const notifRef = (raceId: string, roleKey: string) => doc(db, `races/${raceId}/live_dual_roles`, `${roleKey}_notif`);

const picksOf = (data: unknown): ActivePicks => {
  const picks = data && typeof data === "object" ? (data as { activePicks?: unknown }).activePicks : undefined;
  return picks && typeof picks === "object" ? (picks as ActivePicks) : {};
};

/** Zápis po polích: střelec sn pro uid, jeho ostatní rezervace pryč. */
function pickPatch(picks: ActivePicks, uid: string, startNumber: number): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  Object.keys(picks).forEach((k) => {
    if (picks[k] === uid && k !== String(startNumber)) patch[k] = deleteField();
  });
  patch[String(startNumber)] = uid;
  return patch;
}

/** Spojení chybí nebo transakce kvůli němu selhala – zařadit do fronty. */
const isConnectionError = (err: unknown): boolean => {
  const code = err && typeof err === "object" ? (err as { code?: unknown }).code : undefined;
  return code === "unavailable" || code === "deadline-exceeded" || code === "timeout";
};

const browserOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

/** Transakce s limitem čekání (slabý signál: SDK zkouší znovu i desítky sekund). */
function withTimeout<T>(p: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    p,
    new Promise<T>((_, reject) => {
      timer = setTimeout(() => reject(Object.assign(new Error("Transakce nestihla odpověď serveru."), { code: "timeout" })), TX_WAIT_MS);
    }),
  ]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

/** Rezervace do fronty (bez spojení): jen dotčená pole; slib se splní až potvrzením serveru. */
function queuePick(raceId: string, roleKey: string, uid: string, startNumber: number, knownPicks: ActivePicks): Promise<void> {
  return setDoc(dualRef(raceId, roleKey), { activePicks: pickPatch(knownPicks, uid, startNumber) }, { merge: true });
}

/** Výsledek zápisu ve frontě volajícímu (pás „bez spojení“ zmizí / hláška). */
function reportQueued(write: Promise<void>, onLate?: OnLate): void {
  write.then(
    () => onLate && onLate("saved"),
    () => onLate && onLate("rejected")
  );
}

/** Opuštěná transakce: pokus, který už prošel, se ohlásí; ostatní chyby se zahodí. */
function watchAbandoned<T>(tx: Promise<T>, toLate: (value: T) => LateOutcome | null, onLate?: OnLate): void {
  tx.then(
    (value) => {
      const late = toLate(value);
      if (late && onLate) onLate(late);
    },
    () => undefined
  );
}

/**
 * Rezervace střelce (předchozí vlastní se uvolní). „taken“ = střelce má jiný
 * rozhodčí (se spojením podle serveru, bez něj podle posledního známého
 * stavu `knownPicks`). Bez spojení „queued“ – rezervace čeká ve frontě.
 */
export async function pickDualShooter(
  raceId: string,
  roleKey: string,
  uid: string,
  startNumber: number,
  knownPicks: ActivePicks,
  onLate?: OnLate
): Promise<PickOutcome> {
  const known = knownPicks[String(startNumber)];
  if (known && known !== uid) return "taken";
  if (browserOffline()) {
    reportQueued(queuePick(raceId, roleKey, uid, startNumber, knownPicks), onLate);
    return "queued";
  }
  // Po vypršení limitu se pozdní pokus transakce nesmí zapsat (viz záhlaví)
  let abandoned = false;
  const transaction = runTransaction(db, async (tx) => {
    const ref = dualRef(raceId, roleKey);
    const snap = await tx.get(ref);
    if (abandoned) throw new AbandonedTransaction();
    const picks = picksOf(snap.exists() ? snap.data() : undefined);
    const owner = picks[String(startNumber)];
    if (owner && owner !== uid) return "taken" as const;
    tx.set(ref, { activePicks: pickPatch(picks, uid, startNumber) }, { merge: true });
    return "picked" as const;
  });
  try {
    return await withTimeout(transaction);
  } catch (err) {
    if (!isConnectionError(err)) throw err;
    abandoned = true;
    watchAbandoned(transaction, (v) => v, onLate);
    reportQueued(queuePick(raceId, roleKey, uid, startNumber, knownPicks), onLate);
    return "queued";
  }
}

/** Upozornění pro partnera (bez jména rozhodčího – rozhodnutí majitele 4). */
const notifData = (startNumber: number, shooterName: string, partnerUid: string | undefined) => ({
  takenSN: startNumber,
  takenName: shooterName,
  // Upozornění čte každý přihlášený – jméno rozhodčího se nezveřejňuje,
  // jen obecné „Rozhodčí"; partner uvidí „převzat druhým rozhodčím"
  takenBy: JUDGE_NAME_FALLBACK,
  takenAt: Date.now(),
  forUid: partnerUid || null,
});

/**
 * Převzetí střelce od druhého rozhodčího: upozornění pro něj a rezervace pro
 * sebe – se spojením v jedné transakci, bez něj ve frontě (outcome
 * „queued“). Vrací i rozepsané rány z konceptu {stationId}_{startNumber}.
 */
export async function takeoverDualShooter(
  raceId: string,
  roleKey: string,
  stationId: string,
  uid: string,
  startNumber: number,
  shooterName: string,
  knownPicks: ActivePicks,
  onLate?: OnLate
): Promise<{ outcome: PickOutcome; shots: number[] }> {
  let outcome: PickOutcome = "picked";
  const queue = () => {
    setDoc(notifRef(raceId, roleKey), notifData(startNumber, shooterName, knownPicks[String(startNumber)])).catch(() => undefined);
    reportQueued(queuePick(raceId, roleKey, uid, startNumber, knownPicks), onLate);
    outcome = "queued";
  };
  if (browserOffline()) {
    queue();
  } else {
    // Po vypršení limitu se pozdní pokus nesmí zapsat (převzetí je
    // bezpodmínečné – přepsalo by novější stav a poslalo další upozornění)
    let abandoned = false;
    const transaction = runTransaction(db, async (tx) => {
      const ref = dualRef(raceId, roleKey);
      const snap = await tx.get(ref);
      if (abandoned) throw new AbandonedTransaction();
      const picks = picksOf(snap.exists() ? snap.data() : undefined);
      tx.set(notifRef(raceId, roleKey), notifData(startNumber, shooterName, picks[String(startNumber)]));
      tx.set(ref, { activePicks: pickPatch(picks, uid, startNumber) }, { merge: true });
    });
    try {
      await withTimeout(transaction);
    } catch (err) {
      if (!isConnectionError(err)) throw err;
      abandoned = true;
      watchAbandoned(transaction, () => "picked", onLate);
      queue();
    }
  }
  let shots: number[] = [];
  try {
    const draft = await getDoc(doc(db, `races/${raceId}/live_drafts`, `${stationId}_${startNumber}`));
    const data = draft.exists() ? (draft.data() as { shots?: unknown }).shots : undefined;
    shots = Array.isArray(data) ? (data as number[]) : [];
  } catch {
    shots = [];
  }
  return { outcome, shots };
}

/**
 * Uvolní rezervace rozhodčího (po uložení výsledku, „Uvolnit“, „Změnit
 * roli“): zápis po polích, nikdy transakce; vrací se hned (zápis čeká ve
 * frontě, bez signálu se slib nesplní – nikdo na něj nečeká). Maže podle
 * posledního známého stavu (přijaté riziko – viz záhlaví).
 */
export function releaseDualPick(raceId: string, roleKey: string, uid: string, knownPicks: ActivePicks): void {
  const patch: Record<string, unknown> = {};
  Object.keys(knownPicks).forEach((k) => {
    if (knownPicks[k] === uid) patch[k] = deleteField();
  });
  if (Object.keys(patch).length === 0) return;
  setDoc(dualRef(raceId, roleKey), { activePicks: patch }, { merge: true }).catch(() => undefined);
}
